import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import API from 'core/Api';
import { useModal, useIsInsideModal } from 'store/modal';
import { useFormContext } from 'react-hook-form';
import { createErrorFromResponse } from 'core/Error';
import setServerErrors from 'utils/helpers/formErrors';
import Button from 'components/Button';
import InnerDropzone from './InnerDropzone';

interface IFileDetails {
  name: string;
  id: number;
  success: number;
}

interface IInitialFileDetails {
  id: number;
  name: string;
  size?: number;
}

interface IDropzoneProps {
  placeholder?: string;
  multiple?: boolean;
  accept?: string | string[];
  isImport?: boolean;
  uploadUri?: string;
  deleteFileUri?: string;
  onFilesUploaded?: (files: IFileDetails[]) => void;
  onFilesDeleted?: (file: File) => void;
  props?: any;
  isFileUploading?: (bool: boolean) => void;
  reset?: boolean;
  initialUploadedFiles?: IInitialFileDetails[];
}

/*
 * Abstracted file component, displaying file upload form control.
 */
const Dropzone: FC<IDropzoneProps> = ({
  placeholder,
  multiple,
  accept,
  isImport,
  uploadUri,
  deleteFileUri,
  onFilesUploaded,
  onFilesDeleted,
  isFileUploading,
  props,
  reset,
  initialUploadedFiles,
}) => {
  const [allFiles, setFiles] = useState<File[]>([]);
  const [allFilesWithIds, setFilesWithIds] = useState<IFileDetails[]>([]);
  const [initialFileSizes, setInitialFileSizes] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);
  const { onChange } = props;
  const { t } = useTranslation();
  const { showModal } = useModal();
  const isInsideModal = useIsInsideModal();
  const methods = useFormContext();

  // Global `showModal` errors would hide the modal this form is rendered in
  // (only one modal can be shown at a time), wiping out anything the user
  // already filled in. When nested in a modal, show the error inline instead.
  const showUploadError = (message: string) => {
    if (isInsideModal) {
      setServerErrors({ errors_common: message }, methods.setError);
    } else {
      showModal({ type: 'error', content: message });
    }
  };

  const isTooLargeFileError = (err: any) => {
    const statusCode = err?.response?.status || err?.status;
    const message = String(err?.message || '').toLowerCase();
    // Api.ts rejects with the raw AxiosError (isAxiosError: true) only when there is no
    // server response at all (e.g. request blocked before an HTTP response is generated).
    // When a real server response exists (validation errors, 4xx/5xx with a body), Api.ts
    // rejects with the unwrapped response object instead, which never has `isAxiosError`.
    const isSuppressedUploadNoResponse =
      Boolean(err?.isAxiosError) && Boolean(err?.config?.suppressGlobalServerError) && !err?.response;

    return isSuppressedUploadNoResponse || statusCode === 413 || message.includes('413');
  };

  useEffect(() => {
    if (reset) {
      setFiles([]);
      setFilesWithIds([]);
      setInitialFileSizes({});
    }
  }, [reset]);

  useEffect(() => {
    if (!initialUploadedFiles?.length) return;

    const hydratedFiles = initialUploadedFiles.map(
      file =>
        new File([], file.name, {
          type: 'application/octet-stream',
        })
    );

    const hydratedFileSizes = initialUploadedFiles.reduce((acc, file) => {
      acc[file.name] = Number(file.size || 0);
      return acc;
    }, {} as Record<string, number>);

    const hydratedFilesWithIds = initialUploadedFiles.map(file => ({
      id: file.id,
      name: file.name,
      success: 1,
    }));

    setFiles(hydratedFiles);
    setFilesWithIds(hydratedFilesWithIds);
    setInitialFileSizes(hydratedFileSizes);
    onChange(hydratedFiles);
  }, [initialUploadedFiles]);

  function handleValidator(file: File) {
    if (!isImport) {
      const duplicatedFile = allFiles.find(
        (e: any) => e.name === file.name && e.size === file.size && e.type === file.type
      );
      if (duplicatedFile) {
        showModal({
          type: 'error',
          content: t('fileUploader.duplicatedFile', {
            name: duplicatedFile.name,
          }),
        });
        return {
          code: 'duplicated-file',
          message: t('fileUploader.duplicatedFile', {
            name: duplicatedFile.name,
          }),
        };
      }
    }

    return null;
  }

  useEffect(() => {
    if (isFileUploading) {
      isFileUploading(isUploading);
    }
  }, [isUploading]);

  const {
    // acceptedFiles,
    // fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    validator: handleValidator,
    onDrop: (files: File[]) => {
      setIsUploading(true);

      // Upload request
      let promises: any[] = [];
      if (uploadUri) {
        promises = files.map((file: File) => {
          const formData = new FormData();
          formData.append('file', file);

          // Single upload on import
          if (isImport)
            return API.post(uploadUri, formData, { suppressGlobalServerError: true }).then(res => {
              return {
                success: res.data.success,
                file,
              };
            });

          // Multi upload - catch errors
          return API.post(uploadUri, formData, { suppressGlobalServerError: true })
            .then(res => {
              return {
                id: res.data.id,
                name: res.data.name ? res.data.name : `${res.data.file_name}.${res.data.extension}`,
                file,
              };
            })
            .catch(err => {
              if (isTooLargeFileError(err)) {
                return {
                  isError: true,
                  message: `${file.name} - ${t('fileUploader.tooLargeFile')}`,
                  file,
                };
              }

              const error = createErrorFromResponse(err);

              if (error.errors.errors_common) {
                return {
                  isError: true,
                  message: error.message,
                  file,
                };
              }

              return {
                isError: true,
                message: `${file.name} - ${error.errors.file.join(', ')}`,
                file,
              };
            });
        });
      }

      if (!uploadUri) {
        const mergedFiles = isImport ? files : files.concat(allFiles);
        setFiles(mergedFiles);
        onChange(mergedFiles.length ? mergedFiles : undefined);
        setIsUploading(false);
        return;
      }

      Promise.all(promises)
        .then((values: IFileDetails[]) => {
          const successfulUploads = values.filter((val: any) => !val.isError);
          const successfulFiles = successfulUploads.map((val: any) => val.file).filter(Boolean);

          const nextFiles = isImport ? successfulFiles : [...allFiles, ...successfulFiles];
          setFiles(nextFiles);
          onChange(nextFiles.length ? nextFiles : undefined);

          if (isImport) {
            showModal({
              type: 'success',
              content: <p>{t('common.importSuccess', { count: successfulUploads[0]?.success || 0 })}</p>,
            });
          } else {
            setFilesWithIds(current => [...current, ...successfulUploads]);
            if (onFilesUploaded && successfulUploads.length) {
              onFilesUploaded(successfulUploads);
            }
          }
          // Show errors from multi upload
          const filesErrors = values.map((val: any) => {
            if (val.isError) return val.message;
            return true;
          });
          const failedUploadMessages = filesErrors.filter(er => er !== true);
          if (failedUploadMessages.length) showUploadError(failedUploadMessages.join(', '));
        })
        .catch(err => {
          if (isTooLargeFileError(err)) {
            showUploadError(t('fileUploader.tooLargeFile'));
            return;
          }

          const error = createErrorFromResponse(err);
          const responseData = err?.response?.data || err?.data || {};
          const { success = 0, failed = 0, url } = responseData;
          if (error.errors && !error.errors.errors_common && typeof error.errors !== 'string') {
            // Employees import errors
            if (error.errors.employees) {
              const importErrors = Object.keys(error.errors.employees).map((i: any, index: number) => {
                const text: string[] = [];
                const empError = error.errors.employees[i];
                Object.keys(empError).forEach(ee => {
                  text.push(empError[ee]);
                });
                return `${t('common.row')} ${index + 2} - ${text}`;
              });
              showModal({
                type: 'error',
                content: (
                  <>
                    <p>
                      {t('common.importSuccess', { count: success })}
                      <br />
                      {t('common.importFailed', { count: failed })}
                    </p>
                    <Button
                      btnType="primary"
                      className="mb-4"
                      outlined
                      onClick={() => window.open(url, '_blank')}
                    >
                      {t('common.downloadErrorTemplate')}
                    </Button>
                    <p>
                      {importErrors.map((importErr: string) => (
                        <>
                          {importErr}
                          <br />
                        </>
                      ))}
                    </p>
                  </>
                ),
              });
              return setServerErrors({ errors_common: importErrors }, methods.setError);
            }
          }
          // Common errors
          if (error.errors.errors_common) {
            return setServerErrors({ errors_common: error.errors.errors_common }, methods.setError);
          }

          // Fallback - any other unhandled error (e.g. unexpected server errors)
          return showUploadError(error.message);
        })
        .finally(() => {
          setIsUploading(false);
        });
    },
    multiple,
    accept,
  });

  const removeFile = (file: File) => () => {
    const fileIndex = allFiles.findIndex(currentFile => currentFile === file);

    if (fileIndex < 0) return;

    const fileToDeleteByIndex = allFilesWithIds[fileIndex];
    const newFiles = allFiles.filter((_, index) => index !== fileIndex);
    const newFilesWithIds = allFilesWithIds.filter((_, index) => index !== fileIndex);

    if (onFilesDeleted) {
      onFilesDeleted(file);
    }

    // In case of empty array, set to undefined to trigger form validation.
    if (!newFiles.length) {
      onChange(undefined);
    } else {
      onChange(newFiles);
    }

    setFiles(newFiles);
    setFilesWithIds(newFilesWithIds);

    if (!newFiles.some(currentFile => currentFile.name === file.name)) {
      setInitialFileSizes(current => {
        const next = { ...current };
        delete next[file.name];
        return next;
      });
    }

    if (fileToDeleteByIndex?.id) API.delete(`${deleteFileUri}/${fileToDeleteByIndex.id}`);
  };

  return (
    <>
      <FileControlWrapper {...getRootProps()}>
        <input {...getInputProps()} />
        <p className="mt-5 mb-5">{isDragActive ? t('fileUploader.dropFiles') : placeholder}</p>
        {/* All files */}
        <InnerDropzone
          allFiles={allFiles}
          removeFile={removeFile}
          fileSizes={initialFileSizes}
        />
      </FileControlWrapper>
    </>
  );
};

const FileControlWrapper = styled.div`
  border: 2px dashed #32bfa3;
  border-radius: ${props => props.theme.borderRadius};
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  label {
    text-align: center;
    border: 0;
    padding: 3rem 3rem 4rem;
    cursor: pointer;

    &:after {
      opacity: 0;
    }
  }

  .custom-file-input {
    cursor: pointer;
  }

  .custom-file-input:focus ~ .custom-file-label {
    border: none;
    box-shadow: none;
  }

  .custom-file {
    padding: 3rem 3rem 4rem;
  }
`;

export default Dropzone;
